import React, { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { Link } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

const TITLES = {
  overview: "Accounting Overview",
  budgeting: "A. Budgeting Forecast",
  costs: "B. Actual Cost Ledger",
  sales: "C. Sales Analytics",
  projections: "D. Revenue Projections",
};

/**
 * Cross-event ledger view. Filters costs/sales globally.
 * `mode` values: overview | budgeting | costs | sales | projections
 */
export default function Accounting({ mode = "overview" }) {
  const { fmt } = useAuth();
  const [events, setEvents] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      const { data: evs } = await api.get("/events");
      setEvents(evs);
      const out = [];
      for (const e of evs) {
        try {
          const { data } = await api.get(`/events/${e.id}/summary`);
          out.push({ ...e, ...data });
        } catch {}
      }
      setRows(out);
    })();
  }, []);

  const title = TITLES[mode];
  const cols = mode === "overview"
    ? ["Event", "Date", "Budget", "Actual", "Sales", "Sponsors", "Profit"]
    : mode === "budgeting"
      ? ["Event", "Date", "Budget Forecast"]
      : mode === "costs"
        ? ["Event", "Date", "Actual Cost"]
        : mode === "sales"
          ? ["Event", "Date", "Sales", "Sponsors", "Total Revenue"]
          : ["Event", "Date", "Sales", "Sell-Through", "Projected @ 100%"];

  return (
    <div className="max-w-7xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-widest text-[var(--text-2)] mb-2">Accounting</div>
        <h1 className="font-serif text-4xl">{title}</h1>
        <p className="text-sm text-[var(--text-2)] mt-3 max-w-2xl">
          Cross-event snapshot. Open a specific event to see its detailed ledgers and charts.
        </p>
      </div>

      <div className="card-sharp" data-testid={`accounting-${mode}-table`}>
        <Table>
          <TableHeader>
            <TableRow>
              {cols.map((c, i) => (
                <TableHead key={c} className={`ledger-head ${i >= 2 ? "text-right" : ""}`}>{c}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={cols.length} className="text-center text-sm text-[var(--text-2)] py-8">No events yet</TableCell></TableRow>
            )}
            {rows.map((r) => {
              const projected = r.sell_through_pct
                ? (r.sales_total / (r.sell_through_pct / 100))
                : r.sales_total;
              const base = [
                <Link to={`/events/${r.id}`} className="underline underline-offset-4">{r.name}</Link>,
                <span className="mono text-xs">{r.date}</span>,
              ];
              let cells;
              if (mode === "overview") {
                cells = [...base,
                  <span className="mono text-right">{fmt(r.budget_total)}</span>,
                  <span className="mono text-right text-[var(--accent)]">{fmt(r.actual_total)}</span>,
                  <span className="mono text-right">{fmt(r.sales_total)}</span>,
                  <span className="mono text-right">{fmt(r.sponsors_total)}</span>,
                  <span className={`mono text-right ${r.profit < 0 ? "text-[var(--accent)]" : ""}`}>{fmt(r.profit)}</span>,
                ];
              } else if (mode === "budgeting") {
                cells = [...base, <span className="mono text-right">{fmt(r.budget_total)}</span>];
              } else if (mode === "costs") {
                cells = [...base, <span className="mono text-right">{fmt(r.actual_total)}</span>];
              } else if (mode === "sales") {
                cells = [...base,
                  <span className="mono text-right">{fmt(r.sales_total)}</span>,
                  <span className="mono text-right">{fmt(r.sponsors_total)}</span>,
                  <span className="mono text-right">{fmt(r.sales_total + r.sponsors_total)}</span>,
                ];
              } else {
                cells = [...base,
                  <span className="mono text-right">{fmt(r.sales_total)}</span>,
                  <span className="mono text-right">{r.sell_through_pct?.toFixed(1)}%</span>,
                  <span className="mono text-right">{fmt(projected)}</span>,
                ];
              }
              return (
                <TableRow key={r.id} className="hover:bg-[var(--bg)]">
                  {cells.map((c, i) => <TableCell key={i} className="text-sm">{c}</TableCell>)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
